import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Seo from "@/components/feature/Seo";
import NewsCard from "@/components/feature/NewsCard";
import { hotNews } from "@/mocks/hotNews";
import { getCategory, formatViews } from "@/utils/content";

export default function NewsDetail() {
  const { slug } = useParams();
  const item = hotNews.find((n) => n.slug === slug);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const el = document.getElementById("article-content");
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = el.scrollHeight - window.innerHeight;
      const scrolled = Math.min(Math.max((rect.top * -1) / Math.max(total, 1), 0), 1);
      setProgress(Math.round(scrolled * 100));
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [slug]);

  const related = useMemo(() => {
    if (!item) return [];
    const same = hotNews.filter((n) => n.categoryId === item.categoryId && n.slug !== item.slug);
    const others = hotNews.filter((n) => n.categoryId !== item.categoryId).sort((a, b) => b.heat - a.heat);
    return [...same, ...others].slice(0, 4);
  }, [item]);

  if (!item) {
    return (
      <Layout>
        <Seo title="内容未找到 - 在线吃瓜" description="您访问的新鲜事不存在或已被移动。" path="/news" />
        <section className="container-x flex flex-col items-center py-24 text-center">
          <i className="ri-file-warning-line text-6xl text-background-300"></i>
          <h1 className="mt-4 font-heading text-2xl">这颗瓜好像不见了</h1>
          <p className="mt-2 text-foreground-600">链接可能已失效，去看看其他新鲜事吧。</p>
          <Link to="/news" className="chip mt-6 bg-primary-500 px-6 py-3 text-sm text-background-50 hover:bg-primary-600">返回资讯频道</Link>
        </section>
      </Layout>
    );
  }

  const cat = getCategory(item.categoryId);
  const articleLd = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: item.title,
    description: item.summary,
    image: item.image,
    datePublished: `2025-${item.date}${item.date.split("-")[1] ? `T${item.time}` : "T00:00"}:00+08:00`,
    author: { "@type": "Organization", name: "在线吃瓜" },
    publisher: { "@type": "Organization", name: "在线吃瓜" },
    mainEntityOfPage: `https://www.zgchigua.cn/news/detail/${item.slug}`,
    articleSection: cat.name,
  };
  const crumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://www.zgchigua.cn/" },
      { "@type": "ListItem", position: 2, name: "最新资讯", item: "https://www.zgchigua.cn/news" },
      { "@type": "ListItem", position: 3, name: cat.name, item: `https://www.zgchigua.cn/news?category=${cat.id}` },
      { "@type": "ListItem", position: 4, name: item.title, item: `https://www.zgchigua.cn/news/detail/${item.slug}` },
    ],
  };

  return (
    <Layout>
      <Seo title={`${item.title} - 在线吃瓜`} description={item.summary} keywords={`${item.title},${cat.name},热门话题,新鲜事,在线吃瓜`} type="article" path={`/news/detail/${item.slug}`} image={item.image} jsonLd={[articleLd, crumbLd]} />

      <div className="fixed inset-x-0 top-16 z-40 h-1 bg-background-200">
        <div className="h-full bg-primary-500 transition-all" style={{ width: `${progress}%` }}></div>
      </div>

      <article id="article-content" className="container-x py-8 md:py-10">
        <nav className="flex flex-wrap items-center gap-1 text-xs text-foreground-500" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/news" className="hover:text-primary-600">最新资讯</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to={`/news?category=${cat.id}`} className="hover:text-primary-600">{cat.name}</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="truncate text-foreground-700">{item.title}</span>
        </nav>

        <div className="mx-auto mt-6 max-w-3xl">
          <header>
            <div className="flex flex-wrap items-center gap-2">
              <span className="chip bg-primary-500 px-2.5 py-1 text-xs font-bold text-background-50"><i className={cat.icon}></i>{cat.name}</span>
              <span className="chip bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900">{item.tag}</span>
            </div>
            <h1 className="mt-4 font-heading text-2xl leading-snug md:text-4xl">{item.title}</h1>
            <p className="mt-4 text-base leading-relaxed text-foreground-600 md:text-lg">{item.summary}</p>
            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 border-y border-background-200 py-3 text-xs text-foreground-500">
              <span className="inline-flex items-center gap-1.5"><i className="ri-user-3-fill text-primary-600"></i>编辑：{item.author}</span>
              <span className="inline-flex items-center gap-1.5"><i className="ri-time-line"></i>{item.date} {item.time}</span>
              <span className="inline-flex items-center gap-1.5"><i className="ri-eye-line"></i>阅读 {formatViews(item.views)}</span>
              <span className="inline-flex items-center gap-1.5"><i className="ri-fire-line text-primary-600"></i>热度 {item.heat}</span>
            </div>
          </header>

          <figure className="mt-6 overflow-hidden rounded-2xl">
            <img src={item.image} alt={`${item.title} - ${cat.name}现场图`} className="w-full object-cover" />
          </figure>

          <div className="mt-7 space-y-5">
            {item.paragraphs.map((p, i) => (
              <p key={i} className="leading-loose text-foreground-700">{p}</p>
            ))}
          </div>

          <footer className="mt-8 flex flex-wrap items-center gap-2 rounded-2xl bg-background-100 p-4">
            <span className="text-sm text-foreground-500">分享这颗瓜：</span>
            {[{ i: "ri-weibo-line", l: "微博" }, { i: "ri-wechat-line", l: "微信" }, { i: "ri-qq-line", l: "QQ" }, { i: "ri-link", l: "复制链接" }].map((s) => (
              <button key={s.i} onClick={() => navigator.clipboard?.writeText(window.location.href)} className="chip bg-background-50 border border-background-200 px-3 py-1.5 text-xs text-foreground-600 hover:border-primary-300 hover:text-primary-600 cursor-pointer">
                <i className={s.i}></i>{s.l}
              </button>
            ))}
          </footer>

          <aside className="mt-6 rounded-2xl bg-gradient-to-br from-primary-100 to-secondary-100 p-5">
            <h3 className="font-heading text-lg text-primary-800">关于《在线吃瓜》</h3>
            <p className="mt-2 text-sm text-foreground-700">
              在线吃瓜为你追踪全网最新进展与热门话题，今日热瓜实时榜单让焦点动态即时更新、不错过任何一条劲爆消息。对来源严谨核对，对内容保持理性。
            </p>
            <Link to="/about" className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-primary-700 hover:underline">了解我们 <i className="ri-arrow-right-line"></i></Link>
          </aside>
        </div>
      </article>

      <section className="container-x pb-12">
        <div className="border-t border-background-200 pt-8">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl md:text-2xl">你可能还想吃这些瓜</h2>
            <Link to="/news" className="text-sm font-medium text-primary-600 hover:text-primary-700">更多新鲜事 <i className="ri-arrow-right-line"></i></Link>
          </div>
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {related.map((r) => (
              <NewsCard key={r.slug} item={r} />
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}